import { IRestaurantWorkingTimes, RestaurantWorkingTimes } from '@/shared/model/restaurant-working-times.model';
import { DeliverySpec, IDeliverySpec } from '@/shared/model/delivery-spec.model';
import { MutationTree } from 'vuex';
import { IRestaurant } from '@/shared/model/restaurant.model';
import { IUser } from '@/shared/model/user.model';
import { IDiscount } from '@/shared/model/discount.model';
import { IRating } from '@/shared/model/rating.model';
import { IMenuCategory } from '@/shared/model/menu-category.model';
import { IRestaurantCategory } from '@/shared/model/restaurant-category.model';
import { ICustomer } from '@/shared/model/customer.model';
import { IMarketer, Marketer } from '@/shared/model/marketer.model';
import { IRestaurantStore } from './types';
import { IOrderDetail } from '@/shared/model/order/order-detail.model';

export const mutations: MutationTree<IRestaurantStore> = {
  setRestaurant(state, newRestaurant: IRestaurant): void {
    newRestaurant.marketer = newRestaurant.marketer ?? new Marketer();
    state.restaurant = newRestaurant;
  },
  setId(state, newId: number): void {
    state.restaurant.id = newId;
  },
  setName(state, newName: string): void {
    state.restaurant.name = newName;
  },
  setLogoContentType(state, newLogoContentType: string): void {
    state.restaurant.logoContentType = newLogoContentType;
  },
  setLogo(state, newLogo: string): void {
    state.restaurant.logo = newLogo;
  },
  setImageContentType(state, newImageContentType: string): void {
    state.restaurant.imageContentType = newImageContentType;
  },
  setImage(state, newImage: string): void {
    state.restaurant.image = newImage;
  },
  setZipcode(state, newZipcode: string): void {
    state.restaurant.address.zipcode = newZipcode;
  },
  setStreet(state, newStreet: string): void {
    state.restaurant.address.street = newStreet;
  },
  setAddressAddition(state, newAddressAddition: string): void {
    state.restaurant.address.addressAddition = newAddressAddition;
  },
  setNumber(state, newNumber: string): void {
    state.restaurant.address.number = newNumber;
  },
  setCity(state, newCity: string): void {
    state.restaurant.address.city = newCity;
  },
  setPhone(state, newPhone: string): void {
    state.restaurant.address.phone = newPhone;
  },
  setRegisterNumber(state, newRegisterNumber: string): void {
    state.restaurant.registerNumber = newRegisterNumber;
  },
  setTaxIdentificationNumber(state, newTaxIdentificationNumber: string): void {
    state.restaurant.taxIdentificationNumber = newTaxIdentificationNumber;
  },
  setDeliver(state, newDeliver: boolean): void {
    state.restaurant.deliver = newDeliver;
  },
  setPickup(state, newPickup: boolean): void {
    state.restaurant.pickup = newPickup;
  },
  setBankAccountName(state, newBankAccountName: string): void {
    state.restaurant.bankAccountName = newBankAccountName;
  },
  setIbanNumber(state, newIbanNumber: string): void {
    state.restaurant.ibanNumber = newIbanNumber;
  },
  setEnabled(state, newEnabled: boolean): void {
    state.restaurant.enabled = newEnabled;
  },
  setStartingDate(state, newStartingDate: Date): void {
    state.restaurant.startingDate = newStartingDate;
  },
  setStartingDateString(state, newStartingDateString: string): void {
    state.restaurant.startingDateString = newStartingDateString;
  },
  setDomain(state, newDomain: boolean): void {
    state.restaurant.domain = newDomain;
  },
  setMenuCard(state, newMenuCard: string[]): void {
    state.restaurant.menuCard = newMenuCard;
  },
  setMenuCardPath(state, newMenuCardPath: string): void {
    state.restaurant.menuCardPath = newMenuCardPath;
  },
  setMenuCardContentType(state, newMenuCardContentType: string): void {
    state.restaurant.menuCardContentType = newMenuCardContentType;
  },
  setIdCard(state, newIdCard: string[]): void {
    state.restaurant.idCard = newIdCard;
  },
  setIdCardPath(state, newIdCardPath: string): void {
    state.restaurant.idCardPath = newIdCardPath;
  },
  setIdCardContentType(state, newIdCardContentType: string): void {
    state.restaurant.idCardContentType = newIdCardContentType;
  },
  setCocRegistration(state, newCocRegistration: string[]): void {
    state.restaurant.cocRegistration = newCocRegistration;
  },
  setCocRegistrationPath(state, newCocRegistrationPath: string): void {
    state.restaurant.cocRegistrationPath = newCocRegistrationPath;
  },
  setCocRegistrationContentType(state, newCocRegistrationContentType: string): void {
    state.restaurant.cocRegistrationContentType = newCocRegistrationContentType;
  },
  setBankCard(state, newBankCard: string[]): void {
    state.restaurant.bankCard = newBankCard;
  },
  setBankCardPath(state, newBankCardPath: string): void {
    state.restaurant.bankCardPath = newBankCardPath;
  },
  setBankCardContentType(state, newBankCardContentType: string): void {
    state.restaurant.bankCardContentType = newBankCardContentType;
  },
  setUser(state, newUser: IUser): void {
    state.restaurant.user = newUser;
  },
  setFoodDiscount(state, newFoodDiscount: IDiscount): void {
    state.restaurant.foodDiscount = newFoodDiscount;
  },
  setNonfoodDiscount(state, newNonfoodDiscount: IDiscount): void {
    state.restaurant.nonfoodDiscount = newNonfoodDiscount;
  },
  setRatings(state, newRatings: IRating[]): void {
    state.restaurant.ratings = newRatings;
  },
  setDeliverySpecs(state, newDeliverySpecs: IDeliverySpec[]): void {
    state.restaurant.deliverySpecs = newDeliverySpecs;
  },
  addDeliverySpec(state): void {
    state.restaurant.deliverySpecs.push(new DeliverySpec());
  },
  removeDeliverySpec(state, i: number): void {
    state.restaurant.deliverySpecs.splice(i, 1);
  },
  setRestaurantWorkingTimes(state, newRestaurantWorkingTimes: IRestaurantWorkingTimes[]): void {
    state.restaurant.restaurantWorkingTimes = newRestaurantWorkingTimes;
  },
  addRestaurantWorkingTime(state, { i, deliveryTime }): void {
    deliveryTime.deliverFromHourString
      ? (state.restaurant.restaurantWorkingTimes[i].deliverFromHourString = deliveryTime.deliverFromHourString)
      : (state.restaurant.restaurantWorkingTimes[i].deliverFromHour = new Date());
    deliveryTime.deliverToHourString
      ? (state.restaurant.restaurantWorkingTimes[i].deliverToHourString = deliveryTime.deliverToHourString)
      : (state.restaurant.restaurantWorkingTimes[i].deliverToHour = new Date());
    deliveryTime.pickupFromHourString
      ? (state.restaurant.restaurantWorkingTimes[i].pickupFromHourString = deliveryTime.pickupFromHourString)
      : (state.restaurant.restaurantWorkingTimes[i].pickupFromHour = new Date());
    deliveryTime.pickupToHourString
      ? (state.restaurant.restaurantWorkingTimes[i].pickupToHourString = deliveryTime.pickupToHourString)
      : (state.restaurant.restaurantWorkingTimes[i].pickupToHour = new Date());
  },
  removeRestaurantWorkingTime(state, i: number): void {
    state.workingTimes[i].forEach(wt => {
      wt.deliverFromHour = null;
      wt.deliverFromHourString = null;
      wt.deliverToHour = null;
      wt.deliverToHourString = null;
      wt.pickupFromHour = null;
      wt.pickupFromHourString = null;
      wt.pickupToHour = null;
      wt.pickupToHourString = null;
    });
  },
  removeShift(state, { i, entity, after }: { i: number; entity: string; after: boolean }): void {
    state.workingTimes[i][after ? 1 : 0][entity + 'FromHour'] = null;
    state.workingTimes[i][after ? 1 : 0][entity + 'FromHourString'] = null;
    state.workingTimes[i][after ? 1 : 0][entity + 'ToHour'] = null;
    state.workingTimes[i][after ? 1 : 0][entity + 'ToHourString'] = null;
  },
  setMenuCategories(state, newMenuCategories: IMenuCategory[]): void {
    state.restaurant.menuCategories = newMenuCategories;
  },
  setRestaurantCategories(state, newRestaurantCategories: IRestaurantCategory[]): void {
    state.restaurant.restaurantCategories = newRestaurantCategories;
  },
  setCustomer(state, newCustomer: ICustomer): void {
    state.restaurant.customer = newCustomer;
  },
  setMarketer(state, newMarketer: IMarketer): void {
    state.restaurant.marketer = newMarketer;
  },
  setDateOfBirth(state, newDateOfBirth: Date): void {
    state.restaurant.dateOfBirth = newDateOfBirth;
  },
  updateRestaurantStateMember(state, { stateMember, i, field, value }): void {
    i !== null ? (state.restaurant[stateMember][i][field] = value) : (state.restaurant[stateMember][field] = value);
  },
  setTab(state, newTab: string): void {
    state.tab = newTab;
  },
  setDrawer(state, newDrawer: boolean): void {
    state.drawer = newDrawer;
  },
  setProvideNow(state, newProvideNow: boolean): void {
    state.provideNow = newProvideNow;
  },
  setNotFound(state, newNotFound: string): void {
    state.notFound = newNotFound;
  },
  setPause(state, newPause: boolean): void {
    state.pause = newPause;
    if (!newPause) {
      state.workingTimes.forEach(wt => (wt[1] = new RestaurantWorkingTimes()));
    }
  },
  setReadyOrders(state, newReadyOrders: IOrderDetail[]): void {
    state.orders = newReadyOrders;
  },
  setWorkingTimes(state, newWorkingTimes: IRestaurantWorkingTimes[][]): void {
    state.workingTimes = newWorkingTimes;
  },
  shellPropertyChanged(state, { entity, value, after }: { entity: string; value: string; after: boolean }): void {
    const newDate = parseTime(value);
    state.workingTimes.forEach(wt => {
      if (!after) {
        wt[0][entity] = newDate;
        wt[0][entity + 'String'] = value;
      } else {
        wt[1][entity] = newDate;
        wt[1][entity + 'String'] = value;
      }
    });
  },
  updateWorkingTimesEntity(state, { i, j, entity, value }: { i: number; j: number; entity: string; value: string }) {
    state.workingTimes[i][j][entity] = parseTime(value);
  },
  setPreparingOrdersNumber(state, number: number) {
    state.preparingOrdersNumber = number;
  },
  newOrderArrived(state, order: IOrderDetail) {
    state.orders.push(order);
  },
};

function parseTime(value: string) {
  const newDate = value ? new Date() : null;
  const newHourArray = value?.split(':');
  newDate && newDate.setHours(parseInt(newHourArray[0], 10));
  newDate && newDate.setMinutes(parseInt(newHourArray[1], 10));
  return newDate;
}
